import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AcceptInvite } from "./auth/AcceptInvite";
import { SignIn } from "./auth/SignIn";
import { useSession } from "./auth/useSession";
import { isAcceptRoute } from "./lib/acceptInvite";
import { AssessmentDashboard } from "./screens/AssessmentDashboard";
import { ClientReport } from "./screens/ClientReport";
import { RunReportByInstrument } from "./respond/ByInstrument";
import { Invitations } from "./screens/Invitations";
import { listMyAssignedRuns, listMyMemberships, listOrganisations } from "./lib/api";
import { clientError } from "./lib/clientMessage";
import { isSelfAssessmentMember, viewerMode, type ViewerMode } from "./lib/respondent";
import { supabase } from "./lib/supabase";
import { RespondentApp } from "./respond/RespondentApp";
import { BreadcrumbProvider } from "./shell/ContextHeader";
import { Layout } from "./shell/Layout";
import { ClientDetail } from "./screens/ClientDetail";
import { ClientsList } from "./screens/ClientsList";
import { AssessmentWorkspace } from "./screens/AssessmentWorkspace";
import { EngagementDetail } from "./screens/EngagementDetail";
import { EvaluationResults } from "./screens/EvaluationResults";
import { FindingsActionsRegister } from "./screens/FindingsActionsRegister";
import { RunComparison } from "./screens/RunComparison";
import { RunReport } from "./screens/RunReport";
import { RunOverview } from "./screens/RunOverview";
import { Placeholder } from "./screens/Placeholder";
import { Workspace } from "./screens/Workspace";

export function App() {
  const { session } = useSession();

  // F7 §4.4: the invite callback arrives signed OUT, with a single-use
  // token_hash to exchange (ruling Q3). It must therefore sit BEFORE the session gate below - the
  // gate would render <SignIn/> and the router, which owns every other path,
  // never mounts for a signed-out user. Matched on the raw pathname because
  // it lives outside BrowserRouter on purpose. After the hook call, so the
  // hook order is the same on every render.
  if (isAcceptRoute(window.location.pathname)) return <AcceptInvite />;

  // undefined = the stored session is still being resolved. Rendering the
  // sign-in form during that window flashes it at every returning user.
  if (session === undefined) return null;
  if (session === null) return <SignIn />;

  return <ModeGate userId={session.user.id} userEmail={session.user.email ?? session.user.id} />;
}

// PMO Self-Assessment MVP (decision §6; DESIGN §4.2–§4.3): a respondent gets
// the respondent console and never the consultant workspace. The mode is
// resolved BEFORE either tree mounts, so the workspace never flashes at a
// respondent, and a failure to resolve it fails CLOSED — an error with a
// retry, never a fall-through to the workspace. This is presentation; the
// database's RLS, 0008 and 0019 are the boundary whatever renders here.
function ModeGate({ userId, userEmail }: { userId: string; userEmail: string }) {
  const [mode, setMode] = useState<ViewerMode | { error: string } | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setMode(undefined);
    void (async () => {
      try {
        const [memberships, assigned, organisations] = await Promise.all([
          listMyMemberships(),
          listMyAssignedRuns(),
          listOrganisations(),
        ]);
        if (live) {
          setMode(
            viewerMode(
              memberships.map((m) => m.role),
              assigned,
              isSelfAssessmentMember(memberships, organisations),   // D6
            ),
          );
        }
      } catch (e) {
        // The mode is unknown here, so the viewer is treated as a client:
        // no system text (DISCLOSURE_POLICY V1.1, category 5).
        if (live) setMode({ error: clientError("access", e).message });
      }
    })();
    return () => {
      live = false;
    };
  }, [userId, attempt]);

  if (mode === undefined) return null;
  if (typeof mode === "object") {
    return (
      <div className="card" style={{ maxWidth: 520, margin: "3rem auto" }}>
        <h3 style={{ marginTop: 0 }}>Your access could not be checked</h3>
        <p className="error-banner">{mode.error}</p>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          <button className="btn btn-secondary" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>
    );
  }
  if (mode === "respondent") return <RespondentApp userEmail={userEmail} />;
  return <ConsultantApp userEmail={userEmail} />;
}

function ConsultantApp({ userEmail }: { userEmail: string }) {
  return (
    <BreadcrumbProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout userEmail={userEmail} />}>
            <Route index element={<Workspace />} />
            <Route path="invite" element={<Invitations />} />
            <Route path="clients" element={<ClientsList />} />
            <Route path="clients/:orgId" element={<ClientDetail />} />
            <Route path="clients/:orgId/engagements/:engId" element={<EngagementDetail />} />
            <Route path="clients/:orgId/runs/:runId" element={<RunOverview />} />
            <Route path="clients/:orgId/runs/:runId/assess" element={<AssessmentWorkspace />} />
            <Route path="clients/:orgId/runs/:runId/results" element={<EvaluationResults />} />
            <Route path="clients/:orgId/runs/:runId/register" element={<FindingsActionsRegister />} />
            <Route path="clients/:orgId/runs/:runId/report" element={<RunReport />} />
            <Route path="clients/:orgId/runs/:runId/compare" element={<RunComparison />} />
            <Route path="clients/:orgId/runs/:runId/dashboard" element={<RunReportByInstrument><AssessmentDashboard /></RunReportByInstrument>} />
            <Route path="clients/:orgId/runs/:runId/client-report" element={<RunReportByInstrument><ClientReport /></RunReportByInstrument>} />
            <Route path="actions" element={<Placeholder id="actions" />} />
            <Route path="instruments" element={<Placeholder id="instruments" />} />
            <Route path="admin" element={<Placeholder id="admin" />} />
            <Route
              path="*"
              element={
                <div className="card empty-state">
                  <h3>Page not found</h3>
                  <p>This address does not exist in the console.</p>
                </div>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </BreadcrumbProvider>
  );
}
